function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function isElement(value) {
  return value instanceof Element;
}

export function easeInOutCubic(t) {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export function getScrollableParent(element) {
  if (!isElement(element)) {
    throw new Error('Target must be a DOM element.');
  }

  let parent = element.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;

    const canScrollY =
      /(auto|scroll|overlay)/.test(overflowY) &&
      parent.scrollHeight > parent.clientHeight;

    const canScrollX =
      /(auto|scroll|overlay)/.test(overflowX) &&
      parent.scrollWidth > parent.clientWidth;

    if (canScrollY || canScrollX) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return document.scrollingElement || document.documentElement;
}

function getTargetPositionInContainer(target, container) {
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return {
    top: targetRect.top - containerRect.top + container.scrollTop,
    left: targetRect.left - containerRect.left + container.scrollLeft,
    width: targetRect.width,
    height: targetRect.height,
  };
}

function calculateAlignment({
  start,
  size,
  containerSize,
  scrollOffset,
  alignment,
  offset,
}) {
  const end = start + size;
  const viewportStart = scrollOffset;
  const viewportEnd = scrollOffset + containerSize;

  switch (alignment) {
    case 'start':
      return start + offset;

    case 'center':
      return start - (containerSize / 2 - size / 2) + offset;

    case 'end':
      return end - containerSize + offset;

    case 'nearest':
      if (start < viewportStart && end > viewportEnd) {
        return start + offset;
      }

      if (start < viewportStart) {
        return start + offset;
      }

      if (end > viewportEnd) {
        return end - containerSize + offset;
      }

      return scrollOffset;

    default:
      return start + offset;
  }
}

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function scrollToPosition(container, left, top) {
  container.scrollLeft = left;
  container.scrollTop = top;
}

export async function scrollIntoContainer(
  target,
  container,
  options = {},
  wasCancelled,
  debug = false,
) {
  if (!isElement(target)) {
    throw new Error('Target must be a DOM element.');
  }

  if (!isElement(container)) {
    throw new Error('Container must be a DOM element.');
  }

  const {
    behavior = 'smooth',
    block = 'nearest',
    inline = 'nearest',
    offset = 0,
    easing = easeInOutCubic,
  } = options;

  const duration =
    behavior === 'instant' || behavior === 'auto' || prefersReducedMotion()
      ? 0
      : Math.max(0, Number(options.duration) || 400);

  const targetPosition = getTargetPositionInContainer(target, container);

  const startY = container.scrollTop;
  const startX = container.scrollLeft;
  const EPSILON = 1; // helps with fractional pixels / sub-pixel rounding

  const isFullyVisible =
        targetPosition.top >= startY - EPSILON &&
        
        targetPosition.left >= startX - EPSILON &&
        
        targetPosition.top + targetPosition.height <=
        
        startY + container.clientHeight + EPSILON &&
        
        targetPosition.left + targetPosition.width <=
        
        startX + container.clientWidth + EPSILON;
  
  if (isFullyVisible) {
    return;
  }

  const maxY = Math.max(0, container.scrollHeight - container.clientHeight);
  const maxX = Math.max(0, container.scrollWidth - container.clientWidth);

  const elementIsTallerThanContainer = targetPosition.height > container.clientHeight;
  const elementIsWiderThanContainer = targetPosition.width > container.clientWidth;

  const resolvedBlock = elementIsTallerThanContainer ? 'start' : block;
  const resolvedInline = elementIsWiderThanContainer ? 'start' : inline;

  const destinationY = clamp(
    calculateAlignment({
      start: targetPosition.top,
      size: targetPosition.height,
      containerSize: container.clientHeight,
      scrollOffset: startY,
      alignment: resolvedBlock,
      offset,
    }),
    0,
    maxY,
  );

  const destinationX = clamp(
    calculateAlignment({
      start: targetPosition.left,
      size: targetPosition.width,
      containerSize: container.clientWidth,
      scrollOffset: startX,
      alignment: resolvedInline,
      offset,
    }),
    0,
    maxX,
  );

  if (debug) {
    console.log('[scrollIntoContainer]', {
      startX,
      startY,
      destinationX,
      destinationY,
      block: resolvedBlock,
      inline: resolvedInline,
      duration,
    });
  }

  if (wasCancelled && wasCancelled()) {
    return false;
  }

  if (duration === 0) {
    scrollToPosition(container, destinationX, destinationY);
    return true;
  }

  return new Promise(resolve => {
    let startTime = null;

    function step(timestamp) {
      if (wasCancelled && wasCancelled()) {
        resolve(false);
        return;
      }

      if (startTime == null) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = clamp(elapsed / duration, 0, 1);
      const easedProgress = easing(progress);

      const nextX = startX + (destinationX - startX) * easedProgress;
      const nextY = startY + (destinationY - startY) * easedProgress;

      scrollToPosition(container, nextX, nextY);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        scrollToPosition(container, destinationX, destinationY);
        resolve(true);
      }
    }

    window.requestAnimationFrame(step);
  });
}

/*
 * Compatibility wrapper for older call sites.
 */
export function scrollIntoView(target, options = {}, wasCancelled, debug) {
  const container = getScrollableParent(target);
  return scrollIntoContainer(target, container, options, wasCancelled, debug);
}

export function scrollIntoViewIfNeeded(
  target,
  options = { behavior: 'smooth', block: 'nearest', inline: 'nearest' },
) {
  const container = getScrollableParent(target);
  return scrollIntoContainer(target, container, options);
}
